import React, { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ToastProvider } from './context/ToastContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Spinner, ErrorBoundary } from './components/ui';
import Sidebar from './components/layout/Sidebar';
import LoginScreen from './features/auth/LoginScreen';
import api from './api/client';

// Lazy-loaded pages
const ChangePasswordModal = lazy(() => import('./features/auth/ChangePasswordModal'));
const WelcomeModal = lazy(() => import('./features/auth/WelcomeModal'));
const AdminDashboard = lazy(() => import('./features/dashboard/AdminDashboard'));
const CEODashboard = lazy(() => import('./features/dashboard/CEODashboard'));
const OrgSettings = lazy(() => import('./features/org/OrgSettings'));
const UserManagement = lazy(() => import('./features/users/UserManagement'));
const ManagerTeamView = lazy(() => import('./features/manager/ManagerTeamView'));
const ManagerPerformance = lazy(() => import('./features/manager/ManagerPerformance'));
const EmployeeDashboard = lazy(() => import('./features/employee/EmployeeDashboard'));
const EmployeeReviews = lazy(() => import('./features/employee/EmployeeReviews'));
const EmployeeResources = lazy(() => import('./features/employee/EmployeeResources'));
const EmployeeRecognition = lazy(() => import('./features/employee/EmployeeRecognition'));
const ReviewCycleManagement = lazy(() => import('./features/reviews/ReviewCycleManagement'));
const PendingReviews = lazy(() => import('./features/reviews/PendingReviews'));
const NotificationBell = lazy(() => import('./features/notifications/NotificationPanel'));
const AdminPlanGeneration = lazy(() => import('./features/plans/AdminPlanGeneration'));
const ResourceLibrary = lazy(() => import('./features/resources/ResourceLibrary'));
const AuditLog = lazy(() => import('./features/admin/AuditLog'));
const BadgeManagement = lazy(() => import('./features/admin/BadgeManagement'));

const isAdmin = (role) => role === 'admin' || role === 'super_admin';

const PAGE_TITLES = {
  super_admin: { dashboard: 'Dashboard', org: 'Org Settings', users: 'Users', cycles: 'Review Cycles', reviews: 'Pending Reviews', plans: 'AI Growth Plans', resources: 'Resource Library', badges: 'Badge Management', audit: 'Audit Log' },
  admin: { dashboard: 'Organization Overview', org: 'Org Settings', users: 'Users', cycles: 'Review Cycles', reviews: 'Pending Reviews', plans: 'AI Growth Plans', resources: 'Resource Library', badges: 'Badge Management', audit: 'Audit Log' },
  dept_head: { team: 'My Department', reviews: 'Pending Reviews', performance: 'My Performance' },
  manager: { team: 'My Team', reviews: 'Pending Reviews', performance: 'My Performance' },
  employee: { dashboard: 'My Performance', reviews: 'My Reviews', resources: 'Resources', recognition: 'Recognition' },
};

const DEFAULT_PATH = {
  super_admin: '/dashboard',
  admin: '/dashboard',
  dept_head: '/team',
  manager: '/team',
  employee: '/dashboard',
};

const PageLoader = () => (
  <div className="flex items-center justify-center py-20">
    <Spinner size="lg" />
  </div>
);

function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login, logout, refreshProfile } = useAuth();
  const [showChangePw, setShowChangePw] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.setToken(token);
      refreshProfile().then(u => {
        if (u && (location.pathname === '/' || location.pathname === '/login')) {
          navigate(DEFAULT_PATH[u.role] || '/dashboard', { replace: true });
        }
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (u) => {
    login(u);
    navigate(DEFAULT_PATH[u.role] || '/dashboard', { replace: true });
    const welcomeKey = `pw_welcomed_${u.id}`;
    if (!localStorage.getItem(welcomeKey)) {
      setShowWelcome(true);
      localStorage.setItem(welcomeKey, 'true');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const handleNavigate = (tabId) => {
    navigate(`/${tabId}`);
  };

  if (loading) return <div className="min-h-screen bg-pw-background flex items-center justify-center"><Spinner size="lg" /></div>;

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginScreen onLogin={handleLogin} />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const activeTab = location.pathname.split('/')[1] || 'dashboard';
  const pageTitle = PAGE_TITLES[user.role]?.[activeTab] || 'Dashboard';

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const AdminRoute = ({ children }) => isAdmin(user.role) ? children : <Navigate to={DEFAULT_PATH[user.role]} replace />;
  const ManagerRoute = ({ children }) => ['manager', 'dept_head'].includes(user.role) || isAdmin(user.role) ? children : <Navigate to={DEFAULT_PATH[user.role]} replace />;
  const ReviewerRoute = ({ children }) => ['manager', 'dept_head'].includes(user.role) || isAdmin(user.role) ? children : <Navigate to={DEFAULT_PATH[user.role]} replace />;

  const DashboardPage = () => {
    if (user.role === 'super_admin') return <CEODashboard />;
    if (user.role === 'admin') return <AdminDashboard />;
    if (['manager', 'dept_head'].includes(user.role)) return <ManagerTeamView />;
    return <EmployeeDashboard />;
  };

  return (
    <div className="flex h-dvh bg-pw-background">
      <Sidebar
        user={user}
        activeTab={activeTab}
        onTabChange={handleNavigate}
        onLogout={handleLogout}
        onChangePassword={() => setShowChangePw(true)}
      />
      <main className="flex-1 min-w-0 overflow-y-auto px-3 pt-3 pb-20 lg:pb-3 lg:pl-0 lg:pr-3 lg:py-3">
        {/* Top Nav Bar */}
        <div className="bg-white h-auto lg:h-[3.25rem] rounded-3xl flex flex-col lg:flex-row items-start lg:items-center justify-between px-4 py-3 lg:py-0 shadow-sm animate-slide-up flex-shrink-0 mb-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <img src="/logo.png" alt="Paywize" className="w-7 h-7 rounded-lg lg:hidden" />
            <div className="min-w-0">
              <h1 className="text-sm font-bold text-pw-text">{pageTitle}</h1>
              <p className="text-[0.6875rem] text-pw-hint lg:hidden">
                {getGreeting()}, {user.name?.split(' ')[0]}
              </p>
            </div>
          </div>
          <div className="lg:hidden">
            <Suspense fallback={null}>
              <NotificationBell onNavigate={handleNavigate} />
            </Suspense>
          </div>
          <div className="hidden lg:flex items-center gap-3">
            <p className="text-xs text-pw-muted">
              {getGreeting()}, {user.name?.split(' ')[0]}
            </p>
            <span className="text-[0.625rem] font-semibold text-pw-primary bg-pw-accent-light rounded-full px-2 py-0.5 uppercase">
              {user.designation || user.role}
            </span>
            <Suspense fallback={null}>
              <NotificationBell onNavigate={handleNavigate} />
            </Suspense>
          </div>
        </div>

        {/* Routed Content */}
        <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
        <div className="animate-fade-in">
          <Routes>
            {/* Admin routes */}
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/org" element={<AdminRoute><OrgSettings /></AdminRoute>} />
            <Route path="/users" element={<AdminRoute><UserManagement /></AdminRoute>} />
            <Route path="/cycles" element={<AdminRoute><ReviewCycleManagement /></AdminRoute>} />
            <Route path="/plans" element={<AdminRoute><AdminPlanGeneration /></AdminRoute>} />
            <Route path="/resources" element={isAdmin(user.role) ? <ResourceLibrary /> : <EmployeeResources />} />
            <Route path="/badges" element={<AdminRoute><BadgeManagement /></AdminRoute>} />
            <Route path="/audit" element={<AdminRoute><AuditLog /></AdminRoute>} />

            {/* Reviews — role-aware */}
            <Route path="/reviews" element={
              user.role === 'employee'
                ? <EmployeeReviews />
                : <ReviewerRoute><PendingReviews /></ReviewerRoute>
            } />

            {/* Employee routes */}
            <Route path="/recognition" element={<EmployeeRecognition />} />

            {/* Manager/dept_head routes */}
            <Route path="/team" element={<ManagerRoute><ManagerTeamView /></ManagerRoute>} />
            <Route path="/performance" element={<ManagerRoute><ManagerPerformance /></ManagerRoute>} />

            {/* Catch-all */}
            <Route path="*" element={<Navigate to={DEFAULT_PATH[user.role] || '/dashboard'} replace />} />
          </Routes>
        </div>
        </Suspense>
        </ErrorBoundary>
      </main>
      {showChangePw && <Suspense fallback={null}><ChangePasswordModal onClose={() => setShowChangePw(false)} /></Suspense>}
      {showWelcome && <Suspense fallback={null}><WelcomeModal user={user} onClose={() => setShowWelcome(false)} /></Suspense>}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <AppShell />
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
